import React, { useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { PrimaryButton } from 'src/shared/components/button/Button';
import { Input } from 'src/shared/components/ui/input';
import Loader from 'src/shared/components/loader/Loader';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from 'src/shared/components/ui/sheet';
import { useLazyProjectSitesQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { useGetRiskInsightsSiteStatusQuery } from 'src/app/portfolio/state/api/riskInsightsRefreshEngineSlice';
import {
    useCreateSiteMutation,
    useLazyExportSiteManifestQuery,
    useConvertGeoJSONToGeographyMutation,
    useCreateGeographyMutation,
    type SiteStatus,
} from '../../state/api/organisationEngineSlice';
import { fetchAllDraftSitesForImport, useLazyGetRiskPortfolioImportPreloadQuery } from '../../state/api/riskPortfolioEngineGraphSlice';
import { useGetAdminSitesQuery } from '../../state/api/adminSitesEngineSlice';
import SiteListTable, { type Sort } from '../../components/site-list/SiteListTable';
import type { GqlSiteSortField } from '../../state/api/adminSitesQuery';
import SiteStatusFilter from '../../components/site-status-filter/SiteStatusFilter';
import SiteForm from '../../components/site-form/SiteForm';
import SiteActionsMenu from '../../components/site-actions-menu/SiteActionsMenu';
import SiteManifestUploadDrawer from '../../components/site-manifest-upload-drawer/SiteManifestUploadDrawer';
import { ImportResultCallout } from '../../components/import-result-callout/ImportResultCallout';
import RunRefreshButton from '../../components/run-refresh/RunRefreshButton';
import useImportSites from '../../hooks/useImportSites';
import { exportSiteManifest } from '../../helpers/exportSiteManifest';
import styles from '../../RiskPortfolioAdmin.module.scss';

type OrgSiteManagementProps = {
    currentCustomerId: string;
    projects: Array<{ projectId: string; projectName: string }>;
    statusFilter: SiteStatus;
    onStatusFilterChange: (status: SiteStatus) => void;
};

const PAGE_SIZE = 20;

const COLUMN_SORT_FIELD: Partial<Record<string, GqlSiteSortField>> = {
    name: 'NAME',
    country: 'COUNTRY',
    activity: 'ACTIVITY',
    geography: 'ENVIRONMENT',
    type: 'GEOMETRY_TYPE',
};

const OrgSiteManagement = ({ currentCustomerId, projects, statusFilter, onStatusFilterChange }: OrgSiteManagementProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sort, setSort] = useState<Sort>(null);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [search, setSearch] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleStatusFilterChange = (status: SiteStatus) => {
        setCurrentPage(1);
        onStatusFilterChange(status);
    };

    const handleSortChange = (newSort: Sort) => {
        setCurrentPage(1);
        setSort(newSort);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInputValue(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            setSearch(value);
        }, 300);
    };

    const sortBy = sort ? COLUMN_SORT_FIELD[sort.columnId] : undefined;
    const sortDir = sort ? (sort.direction as 'ASC' | 'DESC') : undefined;

    const { data: sitesResponse, isLoading: isLoadingSites } = useGetAdminSitesQuery({
        businessCentralId: currentCustomerId,
        status: statusFilter,
        page: currentPage,
        pageSize: PAGE_SIZE,
        sortBy,
        sortDir,
        nameContains: search || undefined,
    });
    const sites = sitesResponse?.organisation?.sites?.items ?? [];
    const pagination = sitesResponse?.organisation?.sites?.pagination ?? { totalItems: 0, page: currentPage, pageSize: PAGE_SIZE };

    const { currentData: siteStatusData } = useGetRiskInsightsSiteStatusQuery({ businessCentralId: currentCustomerId });
    const siteSyncStatusById = useMemo(
        () => Object.fromEntries((siteStatusData?.items ?? []).map(item => [item.site_id, item.status])),
        [siteStatusData]
    );

    const [createSiteRaw] = useCreateSiteMutation();
    const [convertGeoJSONToGeographyRaw] = useConvertGeoJSONToGeographyMutation();
    const [createGeographyRaw] = useCreateGeographyMutation();
    const [triggerProjectSites] = useLazyProjectSitesQuery();
    const [triggerImportPreload] = useLazyGetRiskPortfolioImportPreloadQuery();

    const createSite = (req: { bcId: string; body: { name: string; business_central_id: string } }) => createSiteRaw(req).unwrap();
    const fetchProjectSites = (projectId: string) => triggerProjectSites({ projectId }).unwrap();
    const convertGeoJSONToGeography = (input: Parameters<typeof convertGeoJSONToGeographyRaw>[0]) =>
        convertGeoJSONToGeographyRaw(input).unwrap();
    const createGeography = (req: Parameters<typeof createGeographyRaw>[0]) => createGeographyRaw(req).unwrap();
    const fetchExistingDraftSites = (bcId: string) => fetchAllDraftSitesForImport(args => triggerImportPreload(args).unwrap(), bcId);

    const {
        importSites,
        isImporting,
        progress: importProgress,
        result: importResult,
    } = useImportSites({
        createSite,
        fetchProjectSites,
        convertGeoJSONToGeography,
        createGeography,
        fetchExistingDraftSites,
    });

    const [triggerExportSiteManifest] = useLazyExportSiteManifestQuery();
    const [showImportResult, setShowImportResult] = useState(false);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isCreatingSite, setIsCreatingSite] = useState(false);
    const [showManifestUpload, setShowManifestUpload] = useState(false);

    const handleImportFromProjects = async () => {
        await importSites(currentCustomerId, projects);
        setShowImportResult(true);
        onStatusFilterChange('draft');
    };

    const handleCreateSite = async (values: { name: string; business_central_id: string | undefined }) => {
        setIsCreatingSite(true);
        try {
            await createSiteRaw({
                bcId: currentCustomerId,
                body: { name: values.name, business_central_id: values.business_central_id },
            }).unwrap();
            setShowCreateForm(false);
            onStatusFilterChange('draft');
        } finally {
            setIsCreatingSite(false);
        }
    };

    return (
        <div className={styles.orgContent} data-testid='org-site-management'>
            {showImportResult && importResult && <ImportResultCallout result={importResult} onDismiss={() => setShowImportResult(false)} />}
            {isImporting && importProgress && (
                <div className='mb-3 flex flex-col items-center gap-2'>
                    <p className='text-sm text-grey-70'>
                        Importing... ({importProgress.current}/{importProgress.total})
                    </p>
                    <div className='w-64 h-1.5 bg-grey-20 rounded-full overflow-hidden'>
                        <div
                            className='h-full bg-primary rounded-full transition-all duration-200'
                            style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}
            <div className={styles.toolbarRow}>
                <div className='flex items-center gap-3'>
                    <SiteStatusFilter value={statusFilter} onChange={handleStatusFilterChange} />
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-50 pointer-events-none' />
                        <Input
                            value={searchInputValue}
                            onChange={handleSearchChange}
                            placeholder='Search site names...'
                            roundness='Round'
                            className='pl-9'
                        />
                    </div>
                </div>
                <div className={styles.toolbarActions}>
                    <RunRefreshButton />
                    {!showCreateForm && (
                        <PrimaryButton onClick={() => setShowCreateForm(true)} data-testid='create-site-button'>
                            + Create Site
                        </PrimaryButton>
                    )}
                    <SiteActionsMenu
                        onImportFromProjects={handleImportFromProjects}
                        onExportManifest={() => exportSiteManifest(triggerExportSiteManifest, currentCustomerId)}
                        onImportManifest={() => setShowManifestUpload(true)}
                        projectCount={projects.length}
                    />
                </div>
            </div>
            <Sheet open={showCreateForm} onOpenChange={setShowCreateForm}>
                <SheetContent side='right' className='w-[400px] sm:max-w-[400px]'>
                    <SheetHeader>
                        <SheetTitle>Create Site</SheetTitle>
                        <SheetDescription>Add a new site to Global Nature Risk.</SheetDescription>
                    </SheetHeader>
                    <div className='mt-6'>
                        <SiteForm onSubmit={handleCreateSite} onCancel={() => setShowCreateForm(false)} isSubmitting={isCreatingSite} />
                    </div>
                </SheetContent>
            </Sheet>
            <SiteManifestUploadDrawer
                open={showManifestUpload}
                onOpenChange={setShowManifestUpload}
                currentCustomerId={currentCustomerId}
            />
            {isLoadingSites ? (
                <div className={styles.loadingContainer}>
                    <Loader />
                </div>
            ) : (
                <SiteListTable
                    sites={sites}
                    pagination={pagination}
                    onPageChange={setCurrentPage}
                    sort={sort}
                    onSortChange={handleSortChange}
                    siteSyncStatusById={siteSyncStatusById}
                />
            )}
        </div>
    );
};

export default OrgSiteManagement;
